import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { format, parseISO } from "date-fns";

import {
  getRefreshBucketStart,
  type RoomDateTimes,
  useHaNView,
  useHaNViewStore,
  usePreviousTab,
  useRefreshBucket,
  useRememberedRoomsWindows,
  useStoreSelector,
} from "@lib/logic";
import {
  createMeetingRoomRequestKey,
  getAllBuildings,
  getAvailableActivityTypeDataByBuildingId,
  getAvailableDeskAmenityDataByBuildingId,
  getBuildingById,
  getBuildingResources,
  getFloorsByBuildingId,
  getMeetingRoomFloorSuggestionByRequestKeyAndFloorId,
  getWorkspaceOccupancyForBuildingId,
  useAppSelector,
} from "@lib/store";
import { cn } from "@utils";

import { refreshClock } from "../state/refresh/refreshClock";
import { haNSession } from "../state/session/haNSession";
import { useAvailableTabs } from "../useHaNTabs";
import { useHaN } from "./HaNContext";

// Phase 1 only: shows what the state and data layer holds, until phase 2 brings the real shell. Not translated.

type PillStatus = "idle" | "loading" | "updating" | "loaded" | "failed";

const toPillStatus = (status: string | undefined): PillStatus => {
  switch (status?.toLowerCase()) {
    case "loading":
    case "updating":
    case "loaded":
    case "failed":
      return status.toLowerCase() as PillStatus;
    default:
      return "idle";
  }
};

const formatWindow = ({ startDateTime, endDateTime }: RoomDateTimes) => {
  return `${format(parseISO(startDateTime), "HH:mm")} to ${format(parseISO(endDateTime), "HH:mm")}`;
};

const StatusPill = ({ status }: { status: PillStatus }) => {
  return (
    <span
      className={cn("rounded-full px-2 py-0.5 text-xs font-medium", {
        "bg-mapiq-black-100 text-mapiq-black-500": status === "idle",
        "bg-functional-orange/10 text-functional-orange": status === "loading",
        "bg-collaborative-blue-50 text-collaborative-blue-500*": status === "updating",
        "bg-functional-green/10 text-functional-green": status === "loaded",
        "bg-functional-red/10 text-functional-red": status === "failed",
      })}>
      {status}
    </span>
  );
};

const Card = ({ title, status, children }: { title: string; status?: PillStatus; children: ReactNode }) => {
  return (
    <section className="flex flex-col gap-3 rounded-lg bg-white p-4 shadow-mapiq-small">
      <header className="flex items-center justify-between gap-2">
        <h2 className="m-0 text-sm font-semibold">{title}</h2>
        {status && <StatusPill status={status} />}
      </header>
      {children}
    </section>
  );
};

const Rows = ({ children }: { children: ReactNode }) => {
  return <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">{children}</dl>;
};

const Row = ({ label, children }: { label: string; children: ReactNode }) => {
  return (
    <>
      <dt className="text-mapiq-black-500">{label}</dt>
      <dd className="m-0 truncate font-mono text-xs leading-5">{children}</dd>
    </>
  );
};

const DebugButton = ({
  isActive = false,
  onClick,
  children,
}: {
  isActive?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("rounded-md border border-mapiq-black-200 px-2 py-1 text-xs", {
        "bg-mapiq-black-800* text-white": isActive,
        "bg-white hover:bg-mapiq-black-75": !isActive,
      })}>
      {children}
    </button>
  );
};

const DebugHeader = () => {
  const availableTabs = useAvailableTabs();

  return (
    <header className="flex flex-wrap items-center justify-between gap-2">
      <h1 className="m-0 text-base font-semibold">H&N revamp: phase 1 state and data</h1>
      <nav className="flex gap-2">
        {availableTabs.map(tab => (
          <NavLink
            key={tab}
            to={`/now/${tab}`}
            className={({ isActive }) =>
              cn("rounded-md px-3 py-1 text-sm no-underline", {
                "bg-mapiq-black-800* text-white": isActive,
                "bg-white text-mapiq-black-800*": !isActive,
              })
            }>
            {tab}
          </NavLink>
        ))}
      </nav>
    </header>
  );
};

const LocationCard = () => {
  const { building, floor } = useHaN();
  const buildings = useAppSelector(getAllBuildings);
  const buildingName = useAppSelector(state => getBuildingById(state, building.buildingId ?? ""))?.name;
  const floors = useAppSelector(state => getFloorsByBuildingId(state, building.buildingId ?? ""));
  const sortedFloors = [...floors].sort((left, right) => left.number - right.number);

  return (
    <Card
      title="Building and floor"
      status={building.isSettled ? "loaded" : "loading"}>
      <Rows>
        <Row label="Building">{buildingName ?? "none"}</Row>
        <Row label="Settled">{String(building.isSettled)}</Row>
        <Row label="Floor">{floors.find(candidate => candidate.id === floor.floorId)?.name ?? "none"}</Row>
      </Rows>
      <select
        className="rounded-md border border-mapiq-black-200 px-2 py-1 text-sm"
        value={building.buildingId ?? ""}
        onChange={event => building.selectBuilding(event.target.value)}>
        {buildings.map(candidate => (
          <option
            key={candidate.id}
            value={candidate.id}>
            {candidate.name}
          </option>
        ))}
      </select>
      <div className="flex flex-wrap gap-1">
        {sortedFloors.map(candidate => (
          <DebugButton
            key={candidate.id}
            isActive={candidate.id === floor.floorId}
            onClick={() => floor.selectFloor(candidate.id)}>
            {candidate.name}
          </DebugButton>
        ))}
      </div>
    </Card>
  );
};

const RoomsCard = () => {
  const { rooms, filterData } = useHaN();
  const { above, below } = filterData.rooms.adjacentFilteredData;
  const cachedWindowCount = useAppSelector(
    state => Object.keys(state.data.meetingRooms.meetingRoomSuggestionsByRequestKey).length,
  );

  return (
    <Card
      title="Rooms"
      status={toPillStatus(rooms.status)}>
      <Rows>
        <Row label="Showing">{formatWindow(rooms.roomDateTimes)}</Row>
        <Row label="Duration">{rooms.duration} min</Row>
        <Row label="On this floor">{filterData.rooms.filteredData.length}</Row>
        <Row label="Other floors">{above.length + below.length}</Row>
        <Row label="Cached windows">{cachedWindowCount}</Row>
      </Rows>
    </Card>
  );
};

const RefreshCard = () => {
  const bucket = useRefreshBucket(refreshClock);

  return (
    <Card title="Refresh clock">
      <Rows>
        <Row label="Bucket">{bucket}</Row>
        <Row label="Since">{format(getRefreshBucketStart(bucket), "HH:mm")}</Row>
        <Row label="Next refresh">{format(getRefreshBucketStart(bucket + 1), "HH:mm")}</Row>
      </Rows>
      <p className="m-0 text-xs text-mapiq-black-500">Pauses while this browser tab is hidden.</p>
    </Card>
  );
};

const LoadersCard = () => {
  const { building, filterData, usageStatsPerFloor } = useHaN();
  const buildingId = building.buildingId ?? "";
  const occupancy = useAppSelector(state => getWorkspaceOccupancyForBuildingId(state, buildingId));
  const deskAmenitiesStatus = useAppSelector(getAvailableDeskAmenityDataByBuildingId(buildingId)).status;
  const activityTypesStatus = useAppSelector(getAvailableActivityTypeDataByBuildingId(buildingId)).status;
  const resourceCount = useAppSelector(state => getBuildingResources(state, buildingId)?.resources?.length);

  return (
    <Card
      title="Sensors and other data"
      status={toPillStatus(occupancy?.status)}>
      <Rows>
        <Row label="Occupancy">{occupancy ? `${occupancy.states.length} areas` : "not loaded"}</Row>
        <Row label="Requested at">{occupancy ? format(parseISO(occupancy.timestamp), "HH:mm:ss") : "never"}</Row>
        <Row label="Workspaces here">{filterData.workspaces.filteredData.length}</Row>
        <Row label="Desk amenities">{deskAmenitiesStatus}</Row>
        <Row label="Activity types">{activityTypesStatus}</Row>
        <Row label="Explore resources">{resourceCount ?? "not loaded"}</Row>
        <Row label="Usage stats">{usageStatsPerFloor.size} floors</Row>
      </Rows>
    </Card>
  );
};

const RememberedWindowRow = ({ window, index }: { window: RoomDateTimes; index: number }) => {
  const { building, floor } = useHaN();
  const status = useAppSelector(state => {
    if (!building.buildingId || !floor.floorId) return undefined;

    const requestKey = createMeetingRoomRequestKey({ buildingId: building.buildingId, ...window });
    return getMeetingRoomFloorSuggestionByRequestKeyAndFloorId(state, requestKey, floor.floorId)?.status;
  });

  return <Row label={index === 0 ? "Windows" : ""}>{`${formatWindow(window)} (${status ?? "not loaded"})`}</Row>;
};

const SessionCard = () => {
  const previousTab = usePreviousTab(haNSession);
  const rememberedWindows = useRememberedRoomsWindows(haNSession);
  const chosenFloors = useStoreSelector(haNSession, state => state.floorIdByBuildingId);

  return (
    <Card title="Session store">
      <Rows>
        <Row label="Previous tab">{previousTab ?? "none"}</Row>
        <Row label="Chosen floors">{Object.keys(chosenFloors).length} buildings</Row>
        {rememberedWindows.map((window, index) => (
          <RememberedWindowRow
            key={window.startDateTime + window.endDateTime}
            window={window}
            index={index}
          />
        ))}
      </Rows>
      <p className="m-0 text-xs text-mapiq-black-500">Survives leaving H&N. Try the Home page and come back.</p>
    </Card>
  );
};

const ViewCard = () => {
  const { tab, filterData } = useHaN();
  const viewStore = useHaNViewStore();
  const view = useHaNView(state => state);

  const selectFirstItem = () => {
    if (tab === "rooms" && filterData.rooms.filteredData[0]) {
      viewStore.select(tab, { kind: "room", id: filterData.rooms.filteredData[0].id }, { openDetails: true });
    }

    if (tab === "workspaces" && filterData.workspaces.filteredData[0]) {
      viewStore.select(tab, { kind: "area", id: filterData.workspaces.filteredData[0].id }, { openDetails: true });
    }
  };

  return (
    <Card title="View store">
      <Rows>
        <Row label="Rooms">{view.selectionByTab.rooms?.id ?? "none"}</Row>
        <Row label="Workspaces">{view.selectionByTab.workspaces?.id ?? "none"}</Row>
        <Row label="Details open on">{view.detailsTab ?? "none"}</Row>
        <Row label="Hovered">{view.hoveredNodeId ?? "none"}</Row>
      </Rows>
      <div className="flex flex-wrap gap-1">
        <DebugButton onClick={selectFirstItem}>Select first item</DebugButton>
        <DebugButton onClick={() => viewStore.select(tab, null)}>Clear selection</DebugButton>
        <DebugButton onClick={viewStore.closeDetails}>Close details</DebugButton>
      </div>
      <p className="m-0 text-xs text-mapiq-black-500">Per visit: kept across tabs, reset after leaving H&N.</p>
    </Card>
  );
};

export const HaNDebugPanel = () => {
  return (
    <div className="mx-auto flex max-w-screen-xl flex-col gap-4 p-4">
      <DebugHeader />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <LocationCard />
        <RoomsCard />
        <RefreshCard />
        <LoadersCard />
        <SessionCard />
        <ViewCard />
      </div>
    </div>
  );
};
